
/**
 * ============================================================================
 * GSC AUDIT CONTRACT - V2.3.0 (STRICT ENFORCEMENT)
 * AUTHORITY: Senior Quality Assurance Engineer
 * STATUS: RECOVERY V2.3 (FROZEN)
 * ============================================================================
 */

export const CONTRACT_VERSION = "AUDIT_CONTRACT_V2_3";
export const CONTRACT_SIGNATURE = "GSC_SIG_2024_Q4_A23_HARD_VALIDATION";

export const REQUIRED_DIAGNOSTIC_IDS = ["HW_CLOCK", "CORE_PARITY", "DATA_CONTRACT"];

export const TIER_A_IDS = [
    "DSP_CORE_0", "DSP_CORE_1", "WORKER_2", "SCORING_3", 
    "HARMONY_4", "CALIB_5", "INTEGRITY_6", "MEMORY_7"
];

export const TIER_B_IDS = Array.from({ length: 12 }, (_, i) => `STRESS_B_${i}`);
export const TIER_C_IDS = Array.from({ length: 10 }, (_, i) => `GOLDEN_C_${i}`);
export const TIER_D_IDS = ["PERF_0", "PERF_1", "SOAK_0"];

export const ALL_REQUIRED_STRESS_IDS = [...TIER_A_IDS, ...TIER_B_IDS, ...TIER_C_IDS, ...TIER_D_IDS];

export interface AuditValidationResult {
    valid: boolean;
    errors: string[];
    tierPasses: {
        A: boolean;
        B: boolean;
        C: boolean;
        D: boolean;
    };
}

export function validateAuditV2_1(report: any): AuditValidationResult {
    const errors: string[] = [];
    const tierPasses = { A: false, B: false, C: false, D: false };
    
    if (!report.meta) {
        errors.push("MISSING_META");
        return { valid: false, errors, tierPasses };
    }

    if (report.meta.contract_version !== CONTRACT_VERSION) {
        errors.push(`INVALID_CONTRACT_VERSION: Expected ${CONTRACT_VERSION}`);
    }

    const diagnostics = report.diagnostics || [];
    const diagIds = diagnostics.map((d: any) => d.id);
    
    // Check Core Hardware / Pipeline
    const dataContractTest = diagnostics.find((d: any) => d.id === "DATA_CONTRACT");
    if (dataContractTest && !dataContractTest.passed) {
        errors.push("CRITICAL_FUNCTIONAL_AMPUTATION: DSP traces missing in real analysis.");
    }

    REQUIRED_DIAGNOSTIC_IDS.forEach(id => {
        if (!diagIds.includes(id)) errors.push(`DIAGNOSTIC_MISSING:${id}`);
    });

    const stress = report.stress || [];
    const stressIds = stress.map((s: any) => s.id);
    
    ALL_REQUIRED_STRESS_IDS.forEach(id => {
        if (!stressIds.includes(id)) errors.push(`STRESS_TEST_MISSING:${id}`);
    });

    const checkTier = (ids: string[], tierLabel: string) => {
        const tests = stress.filter((s: any) => ids.includes(s.id));
        const allPassed = tests.length === ids.length && tests.every((t: any) => t.passed);
        if (tests.length < ids.length) errors.push(`TIER_${tierLabel}_INCOMPLETE`);
        return allPassed;
    };

    tierPasses.A = checkTier(TIER_A_IDS, "A");
    tierPasses.B = checkTier(TIER_B_IDS, "B");
    tierPasses.C = checkTier(TIER_C_IDS, "C");
    tierPasses.D = checkTier(TIER_D_IDS, "D");

    const valid = errors.length === 0 && tierPasses.A && tierPasses.B && tierPasses.C && tierPasses.D;

    return { valid, errors, tierPasses };
}
